import { useId, type ReactElement } from 'react';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { SubscribeAccount } from './SubscribeAccount';
import { isPublicHttps, webhookUrl } from './webhook-url';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './WebhookSection.module.css';

export const COPIED_TEXT = 'Endereço copiado.';
export const COPY_FAILED_TEXT = 'Não foi possível copiar. Selecione o endereço e copie à mão.';
export const NOT_PUBLIC_TEXT =
  'Este endereço não é público com HTTPS (é local ou usa HTTP simples), então o serviço do WhatsApp não consegue entregar mensagens nele. Para receber mensagens de verdade, abra o CladvApp pelo endereço público do servidor.';

/**
 * Como o serviço do WhatsApp entrega as mensagens recebidas: ele CHAMA um endereço nosso. Mostra o
 * endereço completo (para copiar) e o passo a passo do cadastro; só aparece quando o provedor da
 * instalação usa webhook (`webhookPath`). Quando o provedor exige inscrever a conta no aplicativo
 * (`canSubscribe`), o botão dessa inscrição fica no fim do cartão.
 */
export function WebhookSection({
  path,
  canSubscribe = false,
}: {
  path: string;
  /** O provedor deixa inscrever a conta por um botão (`ConnectionState.canSubscribe`). */
  canSubscribe?: boolean;
}): ReactElement {
  const headingId = useId();
  const inputId = useId();
  const status = useStatusMessage();
  const address = webhookUrl(path);

  const copy = (): void => {
    const done = (ok: boolean): void => status.announce(ok ? COPIED_TEXT : COPY_FAILED_TEXT);
    // Sem permissão ou sem HTTPS o navegador recusa: a pessoa ainda pode selecionar e copiar à mão.
    if (!navigator.clipboard) return done(false);
    navigator.clipboard.writeText(address).then(
      () => done(true),
      () => done(false),
    );
  };

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Recebimento de mensagens (webhook)
      </h3>
      <p className={forms.hint}>
        Para receber as mensagens dos clientes, cadastre o endereço abaixo no painel do serviço do
        WhatsApp (na Meta: seu app, WhatsApp, Configuração, Webhook).
      </p>
      <div className={forms.field}>
        <label htmlFor={inputId} className={forms.label}>
          Endereço de retorno (URL de callback)
        </label>
        <div className={styles.row}>
          <input
            id={inputId}
            className={forms.input}
            readOnly
            value={address}
            spellCheck={false}
            onFocus={(event) => event.currentTarget.select()}
          />
          <Button onClick={copy}>Copiar endereço</Button>
        </div>
      </div>
      <ol className={styles.steps}>
        <li>Cole o endereço acima no campo da URL de retorno (callback) do webhook.</li>
        <li>No token de verificação, cole o mesmo texto que você informou na conexão.</li>
        <li>Verifique e salve; depois assine o campo de mensagens (&ldquo;messages&rdquo;).</li>
      </ol>
      {!isPublicHttps(address) && (
        <p role="note" className={styles.notice}>
          {NOT_PUBLIC_TEXT}
        </p>
      )}
      <StatusRegion message={status.message} />
      {canSubscribe && <SubscribeAccount />}
    </section>
  );
}
